import React, { useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { LuEye } from 'react-icons/lu';
import DetailModal from './DetailModal';

// O hex cadastrado em Variant.color.hex não é confiável hoje — boa parte
// do catálogo está gravada com o mesmo azul genérico (#0000ff)
// independente da cor real (ex: "Marrom", "Verde Musgo", "Rosa Fúcsia"
// todos com esse mesmo hex). Adivinha a cor real pelo NOME em português;
// só usa o hex cadastrado como último recurso, quando nenhuma palavra
// bate.
const COLOR_KEYWORDS = [
  { match: /preto/i, hex: '#1a1a1a' },
  { match: /branco|off ?white/i, hex: '#f5f5f5' },
  { match: /marrom|terracota|castanho/i, hex: '#92400e' },
  { match: /verde/i, hex: '#16a34a' },
  { match: /azul/i, hex: '#2563eb' },
  { match: /vermelho|cramberry/i, hex: '#dc2626' },
  { match: /coral/i, hex: '#ff7f50' },
  { match: /rosa|pink|pitaia|fúcsia|fucsia|chiclete|confete/i, hex: '#ec4899' },
  { match: /amarelo/i, hex: '#eab308' },
  { match: /laranja/i, hex: '#f97316' },
  { match: /roxo|purple|lilás|lilas|lavanda/i, hex: '#9333ea' },
  { match: /cinza|grey|gray|prata|metaliz|degrad|chumbo/i, hex: '#9ca3af' },
  { match: /bege|champagne|areia|nude|rose\b/i, hex: '#d6c3a5' },
  { match: /dourado|gold/i, hex: '#d4af37' },
  { match: /turquesa|turquoise/i, hex: '#06b6d4' },
];

const FALLBACK_COLORS = ['#7c3aed', '#2563eb', '#16a34a', '#ea580c', '#dc2626', '#0891b2', '#a16207'];

function resolveColor(colorName, storedHex, index) {
  const keyword = COLOR_KEYWORDS.find((c) => c.match.test(colorName || ''));
  if (keyword) return keyword.hex;
  if (storedHex) return storedHex;
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

const MODAL_COLUMNS = [
  {
    key: 'swatch',
    label: '',
    format: (_v, row) => (
      <span
        style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: resolveColor(row.colorName, row.hex, 0) }}
      />
    ),
  },
  { key: 'colorName', label: 'Cor' },
  { key: 'quantity', label: 'Unidades', align: 'right' },
];

// Mostra sempre as 5 cores mais vendidas; "Ver tudo" abre modal com a
// lista completa (mesmo padrão de "Itens mais vendidos"/"Estoque").
const SalesByColorChart = ({ data }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const all = data || [];
  const top5 = all.slice(0, 5);
  const total = top5.reduce((sum, p) => sum + p.quantity, 0);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Vendas por cor</h5>
        {all.length > 5 && (
          <button
            type="button"
            className="dashboard-eye-btn"
            onClick={() => setModalOpen(true)}
            aria-label="Ver todas as cores"
          >
            <LuEye size={18} />
          </button>
        )}
      </div>

      {top5.length === 0 ? (
        <p className="text-muted mb-0">Sem vendas nesse período.</p>
      ) : (
        <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center' }}>
          <div style={{ width: '55%', height: '100%' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={top5}
                  dataKey="quantity"
                  nameKey="colorName"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={2}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {top5.map((entry, index) => (
                    <Cell key={entry.colorName} fill={resolveColor(entry.colorName, entry.hex, index)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} itens (${(props.percent * 100).toFixed(1)}%)`,
                    props.payload.colorName,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="list-unstyled mb-0" style={{ width: '45%' }}>
            {top5.map((entry, index) => (
              <li key={entry.colorName} className="d-flex align-items-center mb-2" style={{ fontSize: 13 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: resolveColor(entry.colorName, entry.hex, index),
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
                <span className="text-truncate">{entry.colorName}</span>
                <span className="text-muted ml-auto">
                  {total > 0 ? ((entry.quantity / total) * 100).toFixed(1) : '0.0'}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Vendas por cor — todas"
        columns={MODAL_COLUMNS}
        rows={all}
      />
    </div>
  );
};

export default SalesByColorChart;
